/**
 * Computed properties (spec §9.4, I-8): pure functions of the doc plus `now`; mirror of `computed.py`.
 */
import { Snapshot, active, blocked, depth } from "./structure";
import { DAY_MS, want as wantValue, type WantInputs } from "./want";

export const COMPUTED_KEYS = [
  "want",
  "ageDays",
  "overdueDays",
  "blocked",
  "active",
  "timeSpentMin",
  "childrenDone",
  "childrenTotal",
  "daysSinceDone",
  "depth",
] as const;
export type ComputedKey = (typeof COMPUTED_KEYS)[number];

type TaskRec = Record<string, unknown>;
const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const props = (t: TaskRec) => (t.props as Record<string, unknown> | undefined) ?? {};

export function wantInputs(t: TaskRec): WantInputs {
  const p = props(t);
  return {
    periodDays: num(p["rec.periodDays"]),
    lastDoneAt: str(p["rec.lastDoneAt"]),
    createdAt: str(t.createdAt) ?? "1970-01-01T00:00:00Z",
    elasticity: num(p["rec.elasticity"]),
    minGapDays: num(p["rec.minGapDays"]),
    ceiling: num(p["rec.ceiling"]),
  };
}

export function want(t: TaskRec, nowMs: number): number {
  if (t.typeId !== "recurring") return 0;
  return wantValue(wantInputs(t), nowMs);
}

export function ageDays(t: TaskRec, nowMs: number): number {
  const c = str(t.createdAt);
  return c ? Math.max(0, (nowMs - Date.parse(c)) / DAY_MS) : 0;
}

export function overdueDays(t: TaskRec, nowMs: number): number | null {
  const due = str(props(t).due);
  if (!due || t.doneAt) return null;
  return (nowMs - Date.parse(due)) / DAY_MS;
}

export function daysSinceDone(t: TaskRec, nowMs: number): number | null {
  const anchor = t.typeId === "recurring" ? str(props(t)["rec.lastDoneAt"]) : str(t.doneAt);
  return anchor ? Math.max(0, (nowMs - Date.parse(anchor)) / DAY_MS) : null;
}

export interface TimelogEntry {
  id: string;
  taskId: string;
  start: string;
  end: string | null;
  note?: string;
}

export function timeSpentMin(entries: TimelogEntry[], taskId: string, nowMs: number): number {
  let total = 0;
  for (const e of entries) {
    if (e.taskId !== taskId) continue;
    const end = e.end ? Date.parse(e.end) : nowMs;
    total += Math.max(0, end - Date.parse(e.start));
  }
  return total / 60_000;
}

export function childrenStats(s: Snapshot, id: string): [number, number] {
  const kids = s.childrenOf(id).filter((k) => !s.tasks[k]?.archivedAt);
  return [kids.filter((k) => s.isDone(k)).length, kids.length];
}

export function computeAll(
  s: Snapshot,
  id: string,
  nowMs: number,
  entries: TimelogEntry[] = [],
): Record<ComputedKey, unknown> {
  const t = s.tasks[id];
  const [done, total] = childrenStats(s, id);
  return {
    want: want(t, nowMs),
    ageDays: ageDays(t, nowMs),
    overdueDays: overdueDays(t, nowMs),
    blocked: blocked(s, id),
    active: active(s, id),
    timeSpentMin: timeSpentMin(entries, id, nowMs),
    childrenDone: done,
    childrenTotal: total,
    daysSinceDone: daysSinceDone(t, nowMs),
    depth: depth(s, id),
  };
}
